import React, { useState } from 'react';
import axios from 'axios';

export default function ParticipantImport({ eventId, onImported, onCancel }) {
    const [dragging, setDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.name.endsWith('.csv')) {
            setError("Si prega di caricare un file .csv");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target.result;
                const lines = text.split(/\r?\n/);
                if (lines.length < 1) return;

                // Simple CSV parsing
                const participants = [];
                const headers = lines[0].split(';');

                // Find indices (just in case they vary, though we'll follow the provided format)
                const findIdx = (name) => headers.findIndex(h => h.trim().toLowerCase().startsWith(name.toLowerCase()));

                // Based on user: Nominativo, Email, Ticket ID (or Biglietto), Età (last column)
                // Id;Nominativo;Email;Event ID;Event Name;Prezzo;Stato del pagamento ;Ticket Status;Ticket ID;Biglietto ;Chi vorresti incontrare?;Età

                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line || line.startsWith(';')) continue;

                    const cols = line.split(';');
                    if (cols.length < 3) continue;

                    const fullName = cols[1]?.trim() || '';
                    if (!fullName) continue;

                    const email = cols[2]?.trim() || '';
                    if (!email) continue; // Skip rows without email

                    // Name parsing: First word -> Name, Rest -> Surname
                    const nameParts = fullName.split(' ');
                    const firstName = nameParts[0];
                    const lastName = nameParts.slice(1).join(' ') || '';

                    // Gender parsing
                    // User said: "cercare nel campo Ticket ID il testo UOMO o DONNA"
                    // But in sample it seems to be in column 9 (Biglietto) or column 8 (Ticket ID)
                    const ticketIdCol = cols[8] || '';
                    const bigliettoCol = cols[9] || '';
                    const combinedSearch = (ticketIdCol + ' ' + bigliettoCol).toUpperCase();

                    let sex = 'male'; // default
                    if (combinedSearch.includes('DONNA')) {
                        sex = 'female';
                    } else if (combinedSearch.includes('UOMO')) {
                        sex = 'male';
                    }

                    // Age parsing and conversion to birthDate
                    const age = parseInt(cols[11]) || null;
                    let birthDate = null;
                    if (age) {
                        birthDate = `${new Date().getFullYear() - age}-01-01`;
                    }

                    participants.push({
                        firstName,
                        lastName,
                        email,
                        sex,
                        birthDate,
                        status: 'registered'
                    });
                }

                if (participants.length === 0) {
                    setError("Nessun partecipante trovato nel file.");
                    return;
                }

                setLoading(true);
                await axios.post(`/speeddate/api/events/${eventId}/participants/bulk`, { participants });

                window.Swal.fire({
                    title: 'Importazione Completata!',
                    text: `Caricati con successo ${participants.length} partecipanti.`,
                    icon: 'success',
                    customClass: { popup: 'swal2-glass' }
                });

                if (onImported) onImported();
            } catch (err) {
                console.error("Import error", err);
                setError("Errore durante l'importazione: " + (err.response?.data?.error || err.message));
            } finally {
                setLoading(false);
            }
        };
        reader.readAsText(file);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    return (
        <div
            className={`border-2 border-dashed rounded-4 p-5 text-center transition-all ${dragging ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-light bg-opacity-50'}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            style={{ cursor: 'pointer' }}
            onClick={() => document.getElementById('csvInput').click()}
        >
            <input
                type="file"
                id="csvInput"
                className="d-none"
                accept=".csv"
                onChange={(e) => handleFile(e.target.files[0])}
            />
            {loading ? (
                <div>
                    <div className="spinner-border text-primary mb-3" role="status"></div>
                    <p className="mb-0">Importazione in corso...</p>
                </div>
            ) : (
                <>
                    <i className="fas fa-file-csv fa-3x text-primary mb-3 opacity-50"></i>
                    <h5 className="fw-bold text-dark">Trascina qui il file CSV</h5>
                    <p className="text-muted small">Oppure clicca per selezionarlo dal computer</p>
                    {error && (
                        <div className="alert alert-danger py-2 mt-3 small">
                            <i className="fas fa-exclamation-triangle me-2"></i>{error}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
